import { useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { user } from 'services';
import { deleteFromGreyPlate, updateGreyPlate } from 'services/payments-service';
import { path } from 'router/sections';
import { TableExt, TableInfo, Confirm, themes, GridBox, GridUpdateMessage, GridColumnsSetup } from 'libs/ui';
import { useGridColumns } from 'libs/hooks';
import { getTableParams } from './grid.params';
import GreyPlateForm from './forms/grey-plate-form';

export const GreyPlateGrid = (props) => {
  const { count, entries, className, theme, getXlsAction, history } = props;

  const [showForm, setShowForm] = useState('');
  const [message, setMessage] = useState('');
  const [row, setRow] = useState({});
  const [confirm, setConfirm] = useState(null);

  const { t } = useTranslation();

  // actions
  const addItemAction = () => {
    setRow({});
    setShowForm('addForm');
  };

  /**
   * close confirm
   */
  const closeConfirm = () => {
    setConfirm(null);
  };

  const editItemAction = (row) => {
    setRow(row);
    setShowForm('editForm');
  };

  const reportsAction = (row) => {
    setRow(row);
    history.push(path('rides'), {
      filter: {
        vehicle_plate: row.vehicle_plate,
      },
    });
  };

  /*
   * delete item
   */
  const delItem = (id) => {
    // console.log('🚀 ~ delItem', id);
    deleteFromGreyPlate(id).then(() => {
      props.getEntries({ offset: 0 }, null, true);
      closeConfirm();
    });
  };

  const delItemAction = (row) => {
    setRow(row);
    setConfirm({
      title: t('sections.ГРЗ') + ' ' + row.vehicle_plate + ' ' + t('messages.будет удален. Продолжить?'),
      width: '440px',
      type: 'danger',
      cancelButton: { text: t('buttons.Отмена'), func: closeConfirm },
      acceptButton: { text: t('buttons.Удалить'), func: () => delItem(row.id), theme: themes.red },
    });
  };

  /**
   * close form
   * @returns
   */
  // const closeForm = () => setShowForm('');

  // actions
  const actions = [];
  const rightAdd = user.haveRight('grey_plate.add');
  const rightEdit = user.haveRight('grey_plate.update');
  const rightDel = user.haveRight('grey_plate.delete');

  if (rightDel) {
    actions.push({ title: t('buttons.Удалить'), action: delItemAction });
  }

  if (rightEdit) {
    actions.push({
      isVisible: (row) => row.status !== 'blocked_by_client',
      getTitle: (row) => (!row.is_active ? t('buttons.Активировать') : t('buttons.Деактивировать')),
      action: async (row) => {
        await updateGreyPlate({
          is_active: !row.is_active,
          vehicle_plate: row.vehicle_plate,
          reason: row.reason,
          comment: row.comment.trim(),
          id: row.id,
        });
        props.refresh(true);
      },
    });

    actions.push({
      title: t('buttons.Проезды'),
      action: reportsAction,
    });
  }

  // top actions
  const topActions = [];
  if (rightAdd) {
    topActions.push({ title: t('buttons.Добавить'), icon: 'fa-plus', theme: themes.blue, action: addItemAction });
  }
  if (count) {
    getXlsAction(topActions);
  }

  const initial =
    showForm === 'addForm'
      ? {
          start_at: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          end_at: moment().add(1, 'month').add(-1, 'day').format('YYYY-MM-DD 23:59:59'),
        }
      : { ...row };

  const tableParams = useMemo(() => getTableParams(t), [t]);
  const { getFields, fields: staticFields, ...restTableParams } = tableParams;
  const defaultFields = useMemo(() => (getFields ? getFields(props) : staticFields), [getFields, staticFields, props]);
  const { fields, columns, toggleColumn, reorderColumns, resetColumns } = useGridColumns('grey-plate', defaultFields);
  topActions.push({ cmp: <GridColumnsSetup key="columns-setup" columns={columns} onToggle={toggleColumn} onReorder={reorderColumns} onReset={resetColumns} /> });
  // items
  const items = Object.keys(entries).map((key) => entries[key]);

  return (
    <GridBox>
      <TableInfo theme={theme} count={count} actions={topActions} />

      <TableExt
        theme={theme}
        title={t('grid.Серый список')}
        rows={{ count, items }}
        actions={actions}
        className={className}
        refresh={props.refresh}
        clientId={props.clientId}
        onRowClick={rightEdit ? editItemAction : undefined}
        {...restTableParams}
        fields={fields}
      />

      {showForm && showForm !== 'extForm' && (
        <GreyPlateForm
          type={showForm}
          close={() => setShowForm('')}
          clientId={props.clientId}
          id={row.id}
          meanId={row.mean_id}
          meanType={row.mean_type}
          setMessage={setMessage}
          refresh={props.refresh}
          initialValues={initial}
        />
      )}

      {confirm && <Confirm {...confirm} />}

      {message && <GridUpdateMessage>{message}</GridUpdateMessage>}
    </GridBox>
  );
};
